/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import NoAccessPage from "@components/common/NoAccess";
import RightMenuHeaders from "@components/common/RightMenuHeaders";
import MenuAuditSvgComponent from "../../../../assets/svg-components/left-menu/menuadit";
import SearchableDropdownSelect from "@_common/SearchableDropdown";
import AuditStatusFilter from "../AuditFilter";
import ScrollableReactTable from "@components/ScrollableDatatable";
import Loader from "@components/common/Loader";
import ManageAuditNotfound from "@components/Page-Not-Found/manage-audit-not-found";
import FooterWrapper from "@components/common/FooterWrapper";
import { getValue } from "@utils/lodash";
import { countKeysWithValues } from "@_common/text-helpers";
import { QueryRequestHelper } from "@_common/query-request-helper";
import { allParams } from "@_common/common-helper";
import { convertCurrentDate } from "@_common/date-helpers";
import { handleDownloadOnClick } from "@_common/downloadfile";
import {
  exportManageAudit,
  getManageAuditScoreReport,
  getManageAuditVendorComplianceScoreCard,
  getMISSiteMgmt,
  getMISLineItemWise,
  getLineItemReportList,
  getMISDetails,
  downloadMISReport,
  downloadRetrificationReport,
  downloadMISLineItemReport,
  downloadSiteMGMT,
  downloadMLineItemReport,
} from "@services/audit.service";
import { useDynamicColumns } from "./MISReportsColumns";
import { MISReportsState, FilterParams } from "../Constant/MIS.interface";
import {
  defaultDate,
  getMonthNameWithYear,
  cleanPayload,
  PAGE_FILTERS,
  flattenLineItemData,
  COMPLIANCE_OPTIONS,
} from "../Constant/Util";
import { RadioGroup } from "./LineItemReportList";
import { LINEITEM_COLOUMNS } from "./LineItemReportColumns";
import styled from "styled-components";
import ScrollableReactTable2 from "@components/ScrollableDatatable2";

const CustomTableStyles = styled.div`
  .table2-header-row {
    background-color: #747781 !important;
    height: 40px;
  }
  .table2-header-cell {
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    justify-content: center !important;
    padding: 0 10px !important;
  }
`;

const MISReports: React.FunctionComponent = (props: any) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialMount = useRef(true);
  const getParam = useCallback(
    (key: string) => searchParams.get(key) ?? "",
    [searchParams],
  );
  const viewType = getParam("view_type") || "SITE_MIS";
  const pageNo = parseInt(getParam("page_no") || "1", 10);
  const limitSize = parseInt(getParam("limit") || "100", 10);
  const [siteCount, setSiteCount] = useState("");
  const [state, setState] = useState<MISReportsState>(() => ({
    isLoading: true,
    searchText: getParam("search_text"),
    showSearchTextbox: !!getParam("search_text"),
    selectedIDs: [],
    totalCount: 0,
    list: [],
    listSiteMIS: [],
    listScoreCard: [],
    lineItemWiseMIS: [],
    lineItemReportList: [],
    rectificationReportList: [],
    date: getParam("date") ? new Date(getParam("date")) : defaultDate(),
  }));
  const {
    isLoading,
    searchText,
    showSearchTextbox,
    selectedIDs,
    totalCount,
    list,
    listSiteMIS,
    listScoreCard,
    lineItemWiseMIS,
    lineItemReportList,
    rectificationReportList,
    date,
  } = state;
  const [complianceStatus, setComplianceStatus] = useState("");

  const dateInfo = useMemo(() => {
    const baseDate = getParam("date") ? new Date(getParam("date")) : new Date();
    return {
      baseDate,
      currentMonth: getMonthNameWithYear(baseDate, 0),
      prevMonth1: getMonthNameWithYear(baseDate, 1),
      prevMonth2: getMonthNameWithYear(baseDate, 2),
      prevMonth3: getMonthNameWithYear(baseDate, 3),
      year: baseDate.getFullYear(),
    };
  }, [getParam]);

  const activeList = useMemo(() => {
    switch (viewType) {
      case "SITE_MIS":
        return listSiteMIS;
      case "COMPLIANCE_SCORECARD":
        return listScoreCard;
      case "LINE_ITEM_WISE_REPORT":
        return lineItemWiseMIS;
      case "LINE_ITEM_REPORT":
        return lineItemReportList;
      case "RECTIFICATION_REPORT":
        return rectificationReportList;
      default:
        return list;
    }
  }, [
    viewType,
    listSiteMIS,
    listScoreCard,
    list,
    lineItemWiseMIS,
    lineItemReportList,
    rectificationReportList,
  ]);

  const isSiteMIS = viewType === "SITE_MIS";
  const isLineItemWiseReport = viewType === "LINE_ITEM_WISE_REPORT";
  const paramsObj = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams],
  );

  const columns = useDynamicColumns({
    viewType,
    currentMonthName: dateInfo.currentMonth,
    prevMonth1Name: dateInfo.prevMonth1,
    prevMonth2Name: dateInfo.prevMonth2,
    prevMonth3Name: dateInfo.prevMonth3,
    allParams,
    year: dateInfo.year,
    selectedDate: moment(date).format("YYYY-MM-DD"),
  });

  const buildBasePayload = useCallback(
    (includePagination = true): FilterParams => {
      const params = new URLSearchParams(window.location.search);
      const selectedDate = date;
      const rawDate = params.get("date");
      const month = rawDate
        ? rawDate.split("-")[1]
        : moment(selectedDate).format("MM");
      const year = rawDate
        ? rawDate.split("-")[0]
        : moment(selectedDate).format("YYYY");
      const userSpec = getValue(localStorage, "user", "{}");
      const userId = getValue(JSON.parse(userSpec), "id", "");
      return {
        month,
        year,
        ...(includePagination && {
          page_no: parseInt(params.get("page_no") || "1", 10),
          page_size: parseInt(params.get("limit") || "100", 10),
        }),
        search_text: params.get("search_text") || undefined,
        sort_by: params.get("sort_by") || undefined,
        region_id: params.get("region_id") || undefined,
        group_id: params.get("group_id") || undefined,
        location_id: params.get("location_id") || undefined,
        vendor_id: params.get("vendor_id") || undefined,
        city_id: params.get("city_id") || undefined,
        state_id: params.get("state_id") || undefined,
        audit_type: params.get("audit_type") || undefined,
        audit_status: params.get("audit_status") || undefined,
        status: params.get("status") || undefined,
        legislation_id: params.get("legislation_id") || undefined,
        is_redirect: true,
        user_id: userId,
      };
    },
    [date],
  );

  const fetchData = useCallback(
    async (
      apiCall: (params: string) => Promise<any> | undefined,
      dataMapper: (response: any) => any[],
      totalMapper: (response: any) => number,
      setListKey: keyof Pick<
        MISReportsState,
        | "list"
        | "listSiteMIS"
        | "listScoreCard"
        | "lineItemWiseMIS"
        | "lineItemReportList"
        | "rectificationReportList"
      > = "list",
      includeType: boolean = true,
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const basePayload = buildBasePayload();
        const isRectification = viewType === "RECTIFICATION_REPORT";
        const payload = isRectification
          ? cleanPayload({
              ...basePayload,
              type: "AUDIT_STATUS",
              is_report: "",
              userType: "CLIENT",
            })
          : includeType
            ? cleanPayload({
                ...basePayload,
                ...(viewType === "LINE_ITEM_REPORT" &&
                  complianceStatus?.length > 0 && {
                    status: complianceStatus.includes(",")
                      ? complianceStatus.split(",")
                      : [complianceStatus],
                  }),
                type: viewType,
                is_report: "",
                userType: "CLIENT",
              })
            : cleanPayload({
                ...basePayload,
                ...(viewType === "LINE_ITEM_REPORT" &&
                  complianceStatus?.length > 0 && {
                    status: complianceStatus.includes(",")
                      ? complianceStatus.split(",")
                      : [complianceStatus],
                  }),
                userType: "CLIENT",
              });
        const queryRequest = QueryRequestHelper(payload);
        const response = await apiCall(queryRequest);
        if (!response) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            [setListKey]: [],
            totalCount: 0,
          }));
          return;
        }
        const isSuccess =
          response.statusCode === 200 || response.statusCode === 201;
        if (isSuccess) {
          const mappedData = dataMapper(response);
          const total = totalMapper(response);
          setState((prev) => ({
            ...prev,
            [setListKey]: mappedData,
            totalCount: total,
            isLoading: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            [setListKey]: [],
            totalCount: 0,
          }));
          if (response.message) {
            toast.error(response.message);
          }
        }
      } catch (error: any) {
        if (error?.name !== "AbortError" && error?.code !== "ERR_CANCELED") {
          toast.error("Failed to fetch data. Please try again.");
        }
        setState((prev) => ({
          ...prev,
          isLoading: false,
          [setListKey]: [],
          totalCount: 0,
        }));
      }
    },
    [buildBasePayload, viewType, complianceStatus],
  );

  const getData = useCallback(() => {
    fetchData(
      getManageAuditScoreReport,
      (resp) => {
        if (viewType === "YEAR_HISTORY") {
          return getValue(resp, "data.data", []).map((item: any) => ({
            ...item,
            created_at: moment(item.created_at).format("DD MMM YYYY"),
            status: item.is_active ? "Active" : "Inactive",
            city_name: getValue(item, "city.name", ""),
          }));
        }
        return getValue(resp, "data", []).map((item: any) => ({
          ...item,
          created_at: moment(item.created_at).format("DD MMM YYYY"),
          status: item.is_active ? "Active" : "Inactive",
          city_name: item.city_name ?? "",
        }));
      },
      (resp) => {
        if (viewType === "YEAR_HISTORY") {
          return getValue(resp, "data.pagination.total_records", 0);
        }
        return getValue(resp, "pagination.total", 0);
      },
      "list",
      true,
    );
  }, [fetchData, viewType]);

  const getDataSiteMIS = useCallback(() => {
    fetchData(
      getMISSiteMgmt,
      (resp) =>
        getValue(resp, "data.locactions", []).map((item: any) => ({
          ...item,
          region_name:
            item["Region Name"] === "N/A" ? "-" : (item.region_name ?? ""),
          group_name:
            item["Group Name"] === "N/A" ? "-" : (item.group_name ?? ""),
        })),
      (resp) => getValue(resp, "data.pagination.total", 0),
      "listSiteMIS",
      true,
    );
  }, [fetchData]);

  const getDataComplianceScoreCard = useCallback(() => {
    fetchData(
      getManageAuditVendorComplianceScoreCard,
      (resp) =>
        getValue(resp, "data", []).map((item: any) => ({
          ...item,
          region_name:
            getValue(item, "Region Name", "") === "N/A"
              ? "-"
              : getValue(item, "Region Name", ""),
          group_name:
            getValue(item, "Group Name", "") === "N/A"
              ? "-"
              : getValue(item, "Group Name", ""),
        })),
      (resp) => getValue(resp, "data.pagination.total", 0),
      "listScoreCard",
      false,
    );
  }, [fetchData]);

  const getDataLineItemWiseMIS = useCallback(() => {
    fetchData(
      getMISLineItemWise,
      (resp) => {
        setSiteCount(resp.data.sites_count);
        const legislation = getValue(resp, "data.legislation", []);
        return flattenLineItemData(legislation);
      },
      (resp) => getValue(resp, "data.pagination.total", 0),
      "lineItemWiseMIS",
      true,
    );
  }, [fetchData]);

  const getDataLineItemReport = useCallback(() => {
    fetchData(
      getLineItemReportList,
      (resp) => {
        return getValue(resp, "data.locations", []);
      },
      (resp) => getValue(resp, "data.pagination.total", 0),
      "lineItemReportList",
      true,
    );
  }, [fetchData]);

  const getDataRectificationReport = useCallback(() => {
    fetchData(
      getMISDetails,
      (resp) => getValue(resp, "data.locations", []),
      (resp) => getValue(resp, "data.pagination.total", 0),
      "rectificationReportList",
      true,
    );
  }, [fetchData]);

  const handleNavigateDashboard = useCallback(
    (value: string) => {
      const {
        page_no,
        region_id,
        group_id,
        state_id,
        city_id,
        location_id,
        vendor_id,
        ...rest
      } = Object.fromEntries(searchParams.entries());
      const query = QueryRequestHelper({ ...rest, view_type: value });
      navigate(`${window.location.pathname}?${query}`);
    },
    [navigate, searchParams],
  );

  const toggleSearch = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showSearchTextbox: !prev.showSearchTextbox,
    }));
  }, []);

  const toggleSearchClose = useCallback(() => {
    setState((prev) => ({ ...prev, searchText: "", showSearchTextbox: false }));
    if (getParam("search_text")) {
      const next = new URLSearchParams(searchParams);
      next.delete("search_text");
      navigate(`${window.location.pathname}?${next.toString()}`, {
        replace: true,
      });
    }
  }, [getParam, searchParams, navigate]);

  const handleChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setState((prev) => ({ ...prev, searchText: val }));
      const next = new URLSearchParams(searchParams);
      if (val) {
        next.set("search_text", val);
        next.set("page_no", "1");
      } else {
        next.delete("search_text");
      }
      navigate(`${window.location.pathname}?${next.toString()}`, {
        replace: true,
      });
    },
    [searchParams, navigate],
  );

  const handleSubmitDate = useCallback(
    (newDate: Date) => {
      const formatted = moment(newDate).format("YYYY-MM-DD");
      if (formatted === "Invalid date") {
        toast.error("Please select a valid date");
        return;
      }
      setState((prev) => ({ ...prev, date: newDate }));
      const next = new URLSearchParams(searchParams);
      next.set("date", formatted);
      navigate(`${window.location.pathname}?${next.toString()}`, {
        replace: true,
      });
    },
    [searchParams, navigate],
  );

  const handleChangePagination = useCallback(
    (newPage: string | number) => {
      const next = new URLSearchParams(searchParams);
      next.set("page_no", String(newPage));
      navigate(`${window.location.pathname}?${next.toString()}`);
    },
    [searchParams, navigate],
  );

  const handleChangeLimit = useCallback(
    (newLimit: string | number) => {
      const next = new URLSearchParams(searchParams);
      next.set("limit", String(newLimit));
      next.set("page_no", "1");
      navigate(`${window.location.pathname}?${next.toString()}`);
    },
    [searchParams, navigate],
  );

  const handleTableStateChange = useCallback(
    (value: object) => {
      const selectedIndexList = Object.keys(
        getValue(value, "selectedRowIds", {}),
      ).map(Number);
      const newSelectedIDs = activeList
        .filter((_, i) => selectedIndexList.includes(i))
        .map((item: any) => getValue(item, "id", ""));
      setState((prev) => ({ ...prev, selectedIDs: newSelectedIDs }));
    },
    [activeList],
  );

  const getTrProps = useCallback(() => ({}), []);

  const handleDownloadSelfComplianceReportSITEMIS = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const basePayload = buildBasePayload(false);
      const selectedDate = date;
      const fromDate = getParam("from_date")
        ? new Date(getParam("from_date"))
        : new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const toDate = getParam("to_date")
        ? new Date(getParam("to_date"))
        : new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      const payload = cleanPayload({
        ...basePayload,
        is_redirect: "true",
        report_description: "SITE_MANAGEMENT_REPORT",
        report_type: "SITE_MANAGEMENT_REPORT",
        report_name: `Site Mis - ${convertCurrentDate(
          fromDate,
        )} to ${convertCurrentDate(toDate)}`,
      });
      const resp = await downloadSiteMGMT(payload);
      if (resp) {
        toast.success(getValue(resp, "message", ""));
        navigate("/self-compliance-logs/SITE_MANAGEMENT_REPORT");
      }
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [buildBasePayload, date, getParam, navigate]);

  const handleDownloadSelfComplianceReport = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const basePayload = buildBasePayload(false);
      const MIS_REPORT_CONFIG: Record<
        string,
        {
          reportType: string;
          api: (payload: any) => Promise<any>;
          redirectUrl: string;
          payloadType: string;
        }
      > = {
        LOCATION: {
          reportType: "LOCATION_MIS_AUDIT_SCORE_REPORT",
          api: downloadMISReport,
          redirectUrl: "/self-compliance-logs/LOCATION_MIS_AUDIT_SCORE_REPORT",
          payloadType: "LOCATION",
        },

        REGION: {
          reportType: "REGION_MIS_AUDIT_SCORE_REPORT",
          api: downloadMISReport,
          redirectUrl: "/self-compliance-logs/REGION_MIS_AUDIT_SCORE_REPORT",
          payloadType: "REGION",
        },

        GROUP: {
          reportType: "GROUP_MIS_AUDIT_SCORE_REPORT",
          api: downloadMISReport,
          redirectUrl: "/self-compliance-logs/GROUP_MIS_AUDIT_SCORE_REPORT",
          payloadType: "GROUP",
        },

        VENDOR: {
          reportType: "VENDOR_MIS_AUDIT_SCORE_REPORT",
          api: downloadMISReport,
          redirectUrl: "/self-compliance-logs/VENDOR_MIS_AUDIT_SCORE_REPORT",
          payloadType: "VENDOR",
        },

        RECTIFICATION_REPORT: {
          reportType: "MIS_AUDIT_STATUS_REPORT",
          api: downloadRetrificationReport,
          redirectUrl: "/self-compliance-logs/MIS_AUDIT_STATUS_REPORT",
          payloadType: "AUDIT_STATUS",
        },
      };
      const currentReportConfig = MIS_REPORT_CONFIG[viewType];
      if (currentReportConfig) {
        const payload = cleanPayload({
          ...basePayload,
          report_type: currentReportConfig.reportType,
          report_name:
            viewType === "RECTIFICATION_REPORT"
              ? "Rectification Report"
              : currentReportConfig.reportType,
          type: currentReportConfig.payloadType,
          is_redirect: "true",
        });
        const resp = await currentReportConfig.api(payload);
        if (resp) {
          toast.success(
            getValue(resp, "message", "Export triggered successfully"),
          );
          navigate(currentReportConfig.redirectUrl);
        }
        return;
      }
      const queryRequest = QueryRequestHelper(
        cleanPayload({
          ...basePayload,
          type: viewType,
        }),
      );
      const resp = await exportManageAudit(queryRequest);
      if (resp) {
        toast.success(getValue(resp, "message", ""));
        handleDownloadOnClick(
          getValue(resp, "data.downloadUrl", ""),
          "Manage Audit",
        );
      }
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [buildBasePayload, viewType, navigate]);

  const handleDownloadReportLineItemWise = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const basePayload = buildBasePayload(false);
      const payload = cleanPayload({
        ...basePayload,
        report_type: "MIS_LINE_ITEM_REPORT",
        report_name: "MIS_LINE_ITEM_REPORT",
        legislation_id: getParam("legislation_id") || undefined,
        is_redirect: "true",
        type: getParam("view_type") || "LINE_ITEM_WISE_REPORT",
        userType: "CLIENT",
      });
      const resp = await downloadMISLineItemReport(payload);
      if (resp) {
        toast.success(
          getValue(resp, "message", "Export triggered successfully"),
        );
        const downloadUrl = getValue(resp, "data.downloadUrl", "");
        if (downloadUrl) {
          handleDownloadOnClick(downloadUrl, "MIS_Line_Item_Report");
        }
        navigate("/self-compliance-logs/MIS_LINE_ITEM_REPORT");
      }
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [buildBasePayload, getParam, navigate]);

  const handleDownloadLineItemReport = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const basePayload = buildBasePayload(false);
      const payload = cleanPayload({
        ...basePayload,
        report_type: "LINE_ITEM_REPORT",
        report_name: "LINE_ITEM_REPORT",
        is_redirect: "true",
        userType: "CLIENT",
        ...(complianceStatus?.length > 0 && {
          status: complianceStatus.includes(",")
            ? complianceStatus.split(",")
            : [complianceStatus],
        }),
      });
      const resp = await downloadMLineItemReport(payload);
      if (resp) {
        toast.success(
          getValue(resp, "message", "Export triggered successfully"),
        );
        const downloadUrl = getValue(resp, "data.downloadUrl", "");
        if (downloadUrl) {
          handleDownloadOnClick(downloadUrl, "Line_Item_Report");
        }
        navigate("/self-compliance-logs/LINE_ITEM_REPORT");
      }
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [buildBasePayload, complianceStatus, navigate]);

  useEffect(() => {
    if (!props.permissionAPITriggered) return;
    const currentViewType = getParam("view_type");
    const currentDate = getParam("date");
    if (isInitialMount.current && (!currentViewType || !currentDate)) {
      isInitialMount.current = false;
      const nextParams = new URLSearchParams(searchParams);
      if (!currentViewType) {
        nextParams.set("view_type", PAGE_FILTERS[0].value);
      }
      if (!currentDate) {
        nextParams.set("date", moment(defaultDate()).format("YYYY-MM-DD"));
      }
      navigate(`${window.location.pathname}?${nextParams.toString()}`, {
        replace: true,
      });
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    const fetchDataByType: Record<string, () => void> = {
      SITE_MIS: getDataSiteMIS,
      COMPLIANCE_SCORECARD: getDataComplianceScoreCard,
      LINE_ITEM_WISE_REPORT: getDataLineItemWiseMIS,
      LINE_ITEM_REPORT: getDataLineItemReport,
      RECTIFICATION_REPORT: getDataRectificationReport,
    };
    const fetchFunction = fetchDataByType[viewType];
    if (fetchFunction) {
      fetchFunction();
    } else {
      getData();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    props.permissionAPITriggered,
    viewType,
    getParam("date"),
    pageNo,
    limitSize,
    getParam("region_id"),
    getParam("group_id"),
    getParam("location_id"),
    getParam("vendor_id"),
    getParam("state_id"),
    getParam("city_id"),
    getParam("audit_status"),
    getParam("legislation_id"),
    getDataSiteMIS,
    getDataComplianceScoreCard,
    getData,
    getDataLineItemWiseMIS,
    getDataLineItemReport,
    getDataRectificationReport,
    complianceStatus,
  ]);

  const filterHandlerMap: Record<string, () => void> = {
    SITE_MIS: getDataSiteMIS,
    COMPLIANCE_SCORECARD: getDataComplianceScoreCard,
    LINE_ITEM_WISE_REPORT: getDataLineItemWiseMIS,
    LINE_ITEM_REPORT: getDataLineItemReport,
    RECTIFICATION_REPORT: getDataRectificationReport,
  };

  const hasAccess =
    !isLoading &&
    getValue(props, "subTabPermissionList", []).includes("audit_mis_report");
  if (!hasAccess && !isLoading) {
    return (
      <DashboardLayout
        permissions={getValue(props, "tabPermissionList", [])}
        subTabPermissionList={getValue(props, "subTabPermissionList", [])}
      >
        <NoAccessPage />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      permissions={getValue(props, "tabPermissionList", [])}
      subTabPermissionList={getValue(props, "subTabPermissionList", [])}
    >
      <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div className="header-parent-icon-holder">
            <MenuAuditSvgComponent />
          </div>
          <div className="title-wrapper me-2">
            <h6 className="dashbaord-wrapper__header-parent-title">Audit</h6>
            <h4 className="dashbaord-wrapper__header-title">MIS Reports</h4>
          </div>

          {PAGE_FILTERS.length > 1 && (
            <div className="module-filter-wrapper mt-3 ms-2 manage-audit-view-filter">
              <div className="module_filter_fields_wrapper">
                <label htmlFor="is_configured" className="floating-label">
                  View by
                </label>
                <SearchableDropdownSelect
                  placeholder="Choose View by"
                  options={PAGE_FILTERS}
                  handleChange={(e: any) =>
                    handleNavigateDashboard(getValue(e, "value", ""))
                  }
                  handleClick={(e: any) =>
                    handleNavigateDashboard(getValue(e, "value", ""))
                  }
                  value={
                    PAGE_FILTERS.find((item) => item.value === viewType) ?? {}
                  }
                  className="form-control"
                  name="compliance_type_id"
                  label="label"
                  id="id"
                  removeClose
                />
              </div>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center">
          <RightMenuHeaders
            monthYear={true}
            isYear={viewType !== "YEAR_HISTORY"}
            value={date}
            onChange={handleSubmitDate}
            handleSubmitDate={handleSubmitDate}
            isSearch={false}
            searchInput={searchText}
            showSearchTextbox={showSearchTextbox}
            toggleSearch={toggleSearch}
            toggleSearchClose={toggleSearchClose}
            handleChangeSearch={handleChangeSearch}
            smartFilter={false}
            lineSeperator
            selectedFilterCount={countKeysWithValues(paramsObj)}
            downloadSelfComplianceReport={
              !["YEAR_HISTORY", "COMPLIANCE_SCORECARD"].includes(
                getParam("view_type") as string,
              )
            }
            handleDownloadSelfComplianceReport={
              isSiteMIS
                ? handleDownloadSelfComplianceReportSITEMIS
                : isLineItemWiseReport
                  ? handleDownloadReportLineItemWise
                  : viewType === "LINE_ITEM_REPORT"
                    ? handleDownloadLineItemReport
                    : handleDownloadSelfComplianceReport
            }
            downloadLoading={isLoading}
            viewDownloadedSelfComplianceReports={
              !["YEAR_HISTORY", "COMPLIANCE_SCORECARD"].includes(
                getParam("view_type") as string,
              )
            }
            handleNavigateLogs={() => {
              const reportTypeMap: Record<string, string> = {
                LOCATION: "LOCATION_MIS_AUDIT_SCORE_REPORT",
                REGION: "REGION_MIS_AUDIT_SCORE_REPORT",
                GROUP: "GROUP_MIS_AUDIT_SCORE_REPORT",
                VENDOR: "VENDOR_MIS_AUDIT_SCORE_REPORT",
              };
              let logPath = "/self-compliance-logs/Manage_Audit";
              if (isSiteMIS) {
                logPath = "/self-compliance-logs/SITE_MANAGEMENT_REPORT";
              } else if (isLineItemWiseReport) {
                logPath = "/self-compliance-logs/MIS_LINE_ITEM_REPORT";
              } else if (reportTypeMap[viewType]) {
                logPath = `/self-compliance-logs/${reportTypeMap[viewType]}`;
              } else if (viewType === "RECTIFICATION_REPORT") {
                logPath = "/self-compliance-logs/MIS_AUDIT_STATUS_REPORT";
              } else if (viewType === "LINE_ITEM_REPORT") {
                logPath = "/self-compliance-logs/LINE_ITEM_REPORT";
              }
              navigate(logPath);
            }}
            isApplyButtonRequied={false}
          />
        </div>
      </div>

      <div
        className="main-page-layout-wrapper"
        id={selectedIDs.length > 0 ? "blueNavigationVisible" : ""}
      >
        <div className="admin-wrapper position-relative">
          <AuditStatusFilter
            date={date}
            onFilterChange={filterHandlerMap[viewType] || getData}
            TotalListCount={activeList as []}
            MisType={viewType}
          />
        </div>

        {viewType === "LINE_ITEM_REPORT" && (
          <div className="" style={{ backgroundColor: "#f5f7fa" }}>
            <div
              className="d-flex flex-wrap align-items-center gap-3 px-3 py-2"
              style={{
                backgroundColor: "#b2ebf2",
                borderBottom: "2px solid #0097a7",
              }}
            >
              <RadioGroup
                label="Compliance Status"
                options={COMPLIANCE_OPTIONS}
                selected={complianceStatus}
                onChange={setComplianceStatus}
              />
            </div>
            <div
              className="text-white px-4 py-2 fw-semibold fs-6"
              style={{ backgroundColor: "#00bcd4" }}
            >
              <span style={{ fontWeight: 700 }}>Audit Monitor</span>
              {` Total Sites: ${"-"}   - Audit Month: ${dateInfo.currentMonth}`}
            </div>
          </div>
        )}

        {viewType === "LINE_ITEM_WISE_REPORT" && (
          <div
            className="text-white px-4 py-3 fw-semibold fs-6"
            style={{ backgroundColor: "#00bcd4" }}
          >
            <span style={{ fontWeight: 700 }}>CheckItem MIS-</span>
            {` Total Sites: ${siteCount} - Audit Month: ${dateInfo.currentMonth}`}
          </div>
        )}

        <div style={{ marginTop: "20px" }}>
          {isLoading || !props.permissionAPITriggered ? (
            <Loader />
          ) : activeList.length > 0 ? (
            <CustomTableStyles>
              {isLineItemWiseReport ? (
                <ScrollableReactTable2
                  columns={columns}
                  data={activeList}
                  getTableState={handleTableStateChange}
                  rowClick={() => {}}
                  checkboxlogo
                  check={false}
                  selectedIDs={selectedIDs}
                  getTrProps={getTrProps}
                />
              ) : (
                <ScrollableReactTable
                  columns={
                    viewType === "LINE_ITEM_REPORT"
                      ? LINEITEM_COLOUMNS
                      : columns
                  }
                  data={activeList}
                  getTableState={handleTableStateChange}
                  rowClick={() => {}}
                  checkboxlogo
                  check={false}
                  selectedIDs={selectedIDs}
                  getTrProps={getTrProps}
                />
              )}
            </CustomTableStyles>
          ) : (
            <ManageAuditNotfound />
          )}
        </div>

        <FooterWrapper
          list={activeList}
          totalCount={totalCount}
          limit={limitSize}
          page_no={pageNo}
          handleChangePagination={handleChangePagination}
          handleChangeLimit={handleChangeLimit}
        />
      </div>
    </DashboardLayout>
  );
};

export default MISReports;
